import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Text } from '@mantine/core';
import { formatDate } from '../../format';

/**
 * A date shown as TT.MM.JJJJ that turns into a date field when clicked.
 *
 * Uses the browser's own date input: it returns exactly the YYYY-MM-DD the
 * server expects, brings a picker on every platform, and needs no extra
 * library. The value is committed on blur or Enter, discarded on Escape.
 */
export function DateCell({
  value,
  onCommit,
  disabled,
  pinned,
  title,
  alert,
}: {
  value: string | null;
  onCommit: (next: string) => void;
  disabled?: boolean;
  /** Marks a date the user pinned down, as opposed to a calculated one. */
  pinned?: boolean;
  title?: string;
  /** Shows the date in the warning colour, e.g. a finish that is overdue. */
  alert?: boolean;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const inputRef = useRef<HTMLInputElement>(null);
  // After editing, focus returns to the cell, so keyboard users continue
  // with Tab from where they were instead of from the top of the page.
  const displayRef = useRef<HTMLParagraphElement>(null);
  const refocus = useRef(false);
  useEffect(() => {
    if (!editing && refocus.current) {
      refocus.current = false;
      displayRef.current?.focus();
    }
  }, [editing]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);

  const commit = (): void => {
    setEditing(false);
    if (draft && draft !== value) onCommit(draft);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="date"
        value={draft}
        onChange={(event) => setDraft(event.currentTarget.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            refocus.current = true;
            commit();
          }
          if (event.key === 'Escape') {
            refocus.current = true;
            setDraft(value ?? '');
            setEditing(false);
          }
        }}
        style={{
          width: '100%',
          border: '1px solid var(--mantine-color-brand-5)',
          borderRadius: 4,
          padding: '1px 4px',
          font: 'inherit',
          background: 'var(--mantine-color-body)',
          color: 'var(--mantine-color-text)',
        }}
      />
    );
  }

  return (
    <Text
      ref={displayRef}
      size="sm"
      title={title ?? (disabled ? undefined : 'Zum Ändern anklicken')}
      className={disabled ? 'pst-num' : 'pst-cell pst-num'}
      role={disabled ? undefined : 'button'}
      tabIndex={disabled ? undefined : 0}
      onClick={() => {
        if (!disabled) setEditing(true);
      }}
      onKeyDown={(event) => {
        if (!disabled && (event.key === 'Enter' || event.key === 'F2')) {
          event.preventDefault();
          setEditing(true);
        }
      }}
      style={{
        fontWeight: pinned || alert ? 600 : 400,
        color: alert ? 'var(--mantine-color-red-7)' : undefined,
        textDecoration: pinned ? 'underline dotted' : undefined,
        whiteSpace: 'nowrap',
      }}
    >
      {formatDate(value)}
    </Text>
  );
}
